/* eslint-disable max-statements */
/* eslint-disable max-lines-per-function */
/* eslint-disable camelcase */
import React, { useState } from "react";
import * as TOML from "@ltd/j-toml";
import { Typography, Grid } from "@material-ui/core";
import { Button } from "components/haven-design/buttons";


const availableTypes = [
  "household_member",
  "encrypted_text",
  "date",
  "us_state",
  "array",
  "upload",
  "email",
  "money",
  "phone_number",
  "date_range",
  "multiple",
  "text",
  "boolean",
  "formatted",
  "address",
  "fax_number",
  "select",
];
const typePrefixes = availableTypes.map((type) => `${type}_`);
const typePostfixes = availableTypes.map((type) => `_${type}`);


const guessQuestionTypeByName = (name: string): string | null => {
  const prefixType = typePrefixes.find((prefix) => name.startsWith(prefix));
  if (prefixType) return prefixType.replace("_", "");
  const postfixType = typePostfixes.find((postfix) => name.endsWith(postfix));
  if (postfixType) return postfixType.replace("_", "");
  return null;
};

const questionToQuestionString = (question: string[]): string => `["${question.join("\", \"")}"]`;
const keyToQuestionString = (questionName: string): string => questionToQuestionString(questionName.split("-"));

const validateQuestionByType = (question: any, questionType: string) => {
  const questionString = questionToQuestionString(question.name);
  const errors = [];
  switch (questionType) {
    case "multiple":
      if (!question.data?.options) {
        errors.push(`${questionString} should have question.data.options set`);
      }
      break;
    case "formatted":
      if (!question.data?.content) {
        errors.push(`${questionString} should have question.data.content set`);
      }
      break;
    case "select":
      if (!question.data) {
        errors.push(`${questionString} should have question.data set`);
      } else if (!question.data.options || !question.data.options.length) {
        errors.push(`${questionString} should have at least one question.data.options`);
      }
      break;
    case "array":
      if (!question.data) {
        errors.push(`${questionString} should have question.data set`);
      } else if (!question.data.label) {
        errors.push(`${questionString} should have question.data.label`);
      } else if (!question.data.children || !question.data.children.length) {
        errors.push(`${questionString} should have at least one question.data.children`);
      }
      break;
    default:
      return [];
  }
  return errors;
};
type GroupType = {
  name?: string;
  title?: string;
  description?: string;
  questions?: string[][];
}

const validateLayout = (layout: {group: GroupType[]}[]): string[] => {
  const errors = [];
  // @ts-ignore
  if ((Array.isArray(layout[0].group) && layout[0].group.length)) {
    layout[0].group.forEach((group: GroupType) => {
      if (group.name) {
        if (!group.title) {
          errors.push(`[[layout.group]] ${group.name} should have a title`);
        }
        if (!group.description) {
          errors.push(`[[layout.group]] ${group.name} should have a description`);
        }
      } else {
        errors.push("every [[layout.group]] should have name= ");
      }
    });
  } else {
    errors.push("At least one [[layout.group]] has to be added");
  }
  return errors;
};

const validateWeightCommand = (weight: any, child = false): string[] => {
  const errors = [];
  if (!child) {
    if (!weight.command) return ["command is required for weights"];
    if (!weight.command.value) errors.push("command.value for weight is required");

    if (!["increment", "decrement"].includes(weight.command.operator)) {
      errors.push("command.operator for weight should be increment or decrement");
    }
  }
  return errors;
};
// eslint-disable-next-line func-style
function validateWeightCondition (condition: any): string[] {
  const errors = [];
  if (!condition.type) {
    errors.push("condition should have type");
  } else if (!["and", "or"].includes(condition.type)) {
    errors.push("condition.type should be either \"and\" or \"or\"");
  }
  if (condition.rule) {
    validateWeight(condition.rule, true).forEach((error) => errors.push(error));
  } else {
    errors.push("condition should have rule");
  }
  return errors;
}
// eslint-disable-next-line func-style
function validateWeight (weight: WeightType, child = false): string[] {
  let errors = [] as string[];

  errors = errors.concat(validateWeightCommand(weight, child));
  if (!weight.name) errors.push("weight should have question name. e.g name=[\"first_name\", \"applicant\"]");
  if (weight.operator) {
    if (!["equals", "greater_than", "less_than"].includes(weight.operator)) {
      errors.push("weight.operator should be one of \"equals\", \"greater_than\", \"less_than\"");
    }
  } else {
    errors.push("weight should have operator");
  }
  if (!weight.value) errors.push("weight should have value");
  const conditionErrors = (weight.conditions || [])
    .map(validateWeightCondition)
    .flat(1);
  errors = errors.concat(conditionErrors);
  return errors;
}

const validateWeights = (weights: WeightType[]): string[] =>
  weights.flatMap((weight) => validateWeight(weight));

type Questions = Record<string, string>

type WeightType = {
  name: string[];
  operator: "equals" | "greater_than" | "less_than";
  value: number;
  command: {
    operator: "increment" | "decrement";
    value: number;
  },
  conditions?: {
    type: "and" | "or";
    rule: WeightType[];
  }[];
}

const validateContent = (content:string | undefined, existingQuestions: Questions):string[] => {
  try {
    const errors = [];
    const parsed = TOML.parse[0.5](content || "", "\n");
    if (!(Array.isArray(parsed.question) && parsed.question.length)) {
      errors.push("At least one [[question]] has to be added");
    }
    const layoutErrors = validateLayout(parsed.layout as {group: GroupType[]}[]);
    layoutErrors.forEach((layoutError) => errors.push(layoutError));
    if (parsed.weights) {
      const weightsErrors = validateWeights(parsed.weights as WeightType[]);
      weightsErrors.forEach((weightsError) => errors.push(weightsError));
    }

    const questionsList = parsed.question as Record<string, any>[];
    // ensure all questions are defined in FormQuestions table
    questionsList.forEach((question) => {
      const { name: [questionName] } = question;
      const questionType = existingQuestions[questionName] || guessQuestionTypeByName(questionName);
      if (questionType) {
        validateQuestionByType(question, questionType).forEach((result) => errors.push(result));
      } else {
        errors.push(`${questionName} is not registered in FormQuestions`);
      }
      if (question.cond) {
        if (question.cond.type !== "includes") {
          errors.push(`${questionName} condition type ${question.cond.type} 
            is not supported`);
        }
        if (!question.cond.values || !Array.isArray(question.cond.values)) {
          errors.push(`${questionName} condition should have values list to be set`);
        } else if (!question.cond.values.length) {
          errors.push(`${questionName} condition values should have at least one value`);
        }
      }
    });

    const questionsToggle = questionsList.reduce((acc, { name }) => {
      const key = name.join("-");
      if (key in acc) {
        errors.push(`${keyToQuestionString(key)} is a duplicate key`);
      }
      return ({ ...acc, [key]: false });
    }, {});
    // = question.data?.options?
    const toggledChildren = questionsList.reduce((acc, question) => {
      let optionsChildren = [];
      if (question.data?.options) {
        if (Array.isArray(question.data?.options)) {
          optionsChildren = question.data?.options
            .reduce((optionAcc: any[], option: any) =>
              optionAcc.concat(option.children || []), []);
        } else {
          optionsChildren = question.data.options.children;
        }
      }
      const allChildren = (question.children || [])
        .concat(question.data?.children || [])
        .concat(optionsChildren || []);
      if (allChildren.length && !allChildren.every(Array.isArray)) {
        errors.push(`One of children for question ${question.name} is not correct`);
        return acc;
      }
      return allChildren.reduce((nestedAcc: any, name: any) => ({ ...nestedAcc, [name.join("-")]: true }), acc);
    }, questionsToggle);
    const parsedData = (parsed.layout as {group: any[]}[])[0]
      .group.reduce((acc: any, { questions }: any) =>
        questions.reduce((nestedAcc: any, name: string[]) => {
          const key = name.join("-");
          if (!(key in questionsToggle)) {
            errors.push(`${keyToQuestionString(key)} is in question group 
            but has no [[question]] definition`);
          }
          return ({ ...nestedAcc, [key]: true });
        }, acc), toggledChildren);
    questionsList
      .reduce((acc, { children }) => acc.concat(children || []), [])
      .forEach((childQuestionName: string[]) => {
        if (typeof questionsToggle[childQuestionName.join("-")] === "undefined") {
          errors.push(`["${childQuestionName.join("\", \"")}"] is not defined`);
        }
      });

    Object.entries(parsedData).forEach(([questionName, value]) => {
      if (!value) {
        errors.push(`${keyToQuestionString(questionName)} is not linked 
        to any question or group`);
      }
    });
    return errors;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.log("err", err);
    return [String(err)];
  }
};

type TOMLValidatorProps = {
  content?: string,
  existingQuestions: Record<string, string>
}

const TOMLValidator: React.FC<TOMLValidatorProps> = ({ content, existingQuestions }) => {
  const [validation, setValidation] = useState<string[] | false>(false);
  const validate = () => {
    setValidation(validateContent(content, existingQuestions));
  };
  return (<>
    <Button size="L" onClick={validate} type="button" color={Array.isArray(validation) && validation.length > 0 ? "dangerous" : "primary"} >Validate</Button>
    {Array.isArray(validation) && validation.length === 0 && <Typography variant="body1">Valid</Typography>}

    <Grid container>
      <ul>
        {Array.isArray(validation) && validation.map((errorText, idx) => (
          <li key={`${idx}validationError`}><Typography variant="body1">{errorText}</Typography></li>))}
      </ul>
    </Grid>
  </>);
};

export { validateContent };

export default TOMLValidator;
